import { tenantApi } from '@shared/api/tenantApi';

/**
 * Facilities & bookings API — the "Book" tab.
 *
 * Endpoints are the member-facing tenant-api `/public/facilities/*` routes
 * (verified against tenant-api `routes/index.ts`). Responses are envelope
 * wrapped `{ data, success }`; we unwrap `.data` here so callers see plain
 * payloads. Mirrors the live Angular portal `FacilitiesService`.
 */

export type FacilityBookingMode = 'internal' | 'external_link';
export type FacilityOpenMethod = 'browser' | 'iframe';
export type FacilityBookingStatus =
  | 'pending'
  | 'confirmed'
  | 'approved'
  | 'rejected'
  | 'cancelled'
  | 'completed';

export interface Facility {
  facility_id: string;
  tenant_id: string;
  location_id: string;
  location_name?: string | null;
  location_code?: string | null;
  location_address?: string | null;
  location_city?: string | null;
  location_region?: string | null;
  location_postal_code?: string | null;
  location_country_code?: string | null;
  location_latitude?: string | number | null;
  location_longitude?: string | number | null;
  parent_location_id?: string | null;
  name: string;
  description?: string | null;
  notes?: string | null;
  colour?: string | null;
  enabled: boolean;
  booking_mode: FacilityBookingMode;
  external_open_method: FacilityOpenMethod;
  members_only: boolean;
  community_only: boolean;
  require_approval: boolean;
  allow_cancel: boolean;
  max_booking_attendees?: number | null;
  slot_interval_minutes: number;
  photo_url?: string | null;
}

export interface FacilityLaunch {
  url: string;
  open_method: FacilityOpenMethod;
}

export interface FacilityAvailability {
  id: string;
  resource_type: 'facility';
  resource_id: string;
  starts_at: string;
  ends_at: string;
  is_available: boolean;
  source?: string | null;
  timezone?: string | null;
}

export interface FacilityBookingPayload {
  starts_at: string;
  ends_at: string;
  attendee_count?: number | null;
  notes?: string | null;
  property_location_id?: string | null;
}

export interface FacilityBooking {
  facility_booking_id: string;
  facility_id: string;
  facility_name?: string | null;
  member_id: string;
  starts_at: string;
  ends_at: string;
  attendee_count?: number | null;
  notes?: string | null;
  status: FacilityBookingStatus;
  approval_required: boolean;
  cancelled_at?: string | null;
  cancellation_reason?: string | null;
  created_at?: string;
}

interface Envelope<T> {
  data: T;
  success?: boolean;
}

const SMOKE_FLOW_DESCRIPTION_RE = /^(?:Created|Edited) through the stepper smoke flow\b/i;

function sanitizeFacility(facility: Facility): Facility {
  const description = facility.description?.trim();
  return {
    ...facility,
    description: description && SMOKE_FLOW_DESCRIPTION_RE.test(description) ? null : facility.description,
  };
}

export async function fetchFacilities(): Promise<Facility[]> {
  const { data } = await tenantApi.get<Envelope<Facility[]>>('/public/facilities');
  return (data.data ?? []).map(sanitizeFacility);
}

export async function fetchFacility(facilityId: string): Promise<Facility> {
  const { data } = await tenantApi.get<Envelope<Facility>>(`/public/facilities/${facilityId}`);
  return sanitizeFacility(data.data);
}

export async function fetchFacilityLaunch(facilityId: string): Promise<FacilityLaunch> {
  const { data } = await tenantApi.get<Envelope<FacilityLaunch>>(
    `/public/facilities/${facilityId}/launch`,
  );
  return data.data;
}

export async function fetchFacilityAvailability(
  facilityId: string,
  params?: { from?: string; to?: string; is_available?: boolean },
): Promise<FacilityAvailability[]> {
  const { data } = await tenantApi.get<Envelope<FacilityAvailability[]>>(
    `/public/facilities/${facilityId}/availability`,
    { params },
  );
  return data.data ?? [];
}

export async function fetchFacilityAvailabilityMonths(
  facilityId: string,
  params?: { from?: string; to?: string; is_available?: boolean },
): Promise<string[]> {
  const { data } = await tenantApi.get<Envelope<string[]>>(
    `/public/facilities/${facilityId}/availability-months`,
    { params },
  );
  return data.data ?? [];
}

export async function createFacilityBooking(
  facilityId: string,
  payload: FacilityBookingPayload,
): Promise<FacilityBooking> {
  const { data } = await tenantApi.post<Envelope<FacilityBooking>>(
    `/public/facilities/${facilityId}/bookings`,
    payload,
  );
  return data.data;
}

export async function fetchMyBookings(): Promise<FacilityBooking[]> {
  const { data } = await tenantApi.get<Envelope<FacilityBooking[]>>(
    '/public/facilities/my-bookings',
  );
  return data.data ?? [];
}

export async function cancelFacilityBooking(
  bookingId: string,
  reason?: string | null,
): Promise<FacilityBooking> {
  const { data } = await tenantApi.patch<Envelope<FacilityBooking>>(
    `/public/facilities/bookings/${bookingId}/cancel`,
    { reason: reason ?? null },
  );
  return data.data;
}

export async function clearFacilityBooking(bookingId: string): Promise<void> {
  await tenantApi.patch(`/public/facilities/bookings/${bookingId}/clear`);
}
